import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "panel", "form", "exportLink", "count",
    "text", "checkbox", "select", "number",
    "exportSortActions", "applyActions"
  ]
  static values = {
    baseUrl: String,
    type: String
  }

  connect() {
    this._timer = null
    this.updateCount()
    this.updateExportHref()

    this.formTarget.addEventListener("submit", (e) => {
      e.preventDefault()
      this.apply()
    })
  }

  togglePanel() {
    if (!this.hasPanelTarget) return
    const open = this.panelTarget.classList.toggle("hidden")

    if (this.hasExportSortActionsTarget && this.hasApplyActionsTarget) {
      if (!open) {
        // Panel is now visible → show Apply/Reset, hide Export/Sort
        this.exportSortActionsTarget.classList.add("hidden")
        this.applyActionsTarget.classList.remove("hidden")
      } else {
        // Panel hidden → restore Export/Sort
        this.exportSortActionsTarget.classList.remove("hidden")
        this.applyActionsTarget.classList.add("hidden")
      }
    }
  }

  clearAll(event) {
    event?.preventDefault()

    // Reset inputs
    this.textTargets.forEach(i => i.value = "")
    this.numberTargets.forEach(i => i.value = "")
    this.checkboxTargets.forEach(i => { i.checked = false })
    this.selectTargets.forEach(s => { Array.from(s.options).forEach(o => o.selected = false) })

    // Ask the category-filter controller to re-hide subgroups
    this.element.dispatchEvent(new CustomEvent("filters:sync", { bubbles: true }))

    // Update UI counters/links immediately
    this.updateCount()
    const url = this.unfilteredUrl()      // <- go to base (preserve tab & sort)
    this.updateExportHref(url)

    // Navigate so the parts table reloads
    if (window.Turbo?.visit) {
      Turbo.visit(url, { action: "replace" })
    } else {
      window.location.assign(url)
    }
  }

  // Called by inputs/selects on change; updates count/export only (no navigation)
  onInputChanged() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this.updateCount()
      this.updateExportHref(this.buildUrl())
    }, 150)
  }

  // ---- Core ----
  apply() {
    const url = this.buildUrl()
    if (window.Turbo?.visit) {
      Turbo.visit(url, { action: "replace" })
    } else {
      window.location.assign(url)
    }
  }

  // Build URL with current form data
  buildUrl() {
    const base = new URL(this.baseUrlValue, window.location.origin)
    const fd = new FormData(this.formTarget)

    // preserve current tab
    const currentType = this.typeValue || new URLSearchParams(window.location.search).get("type")
    if (currentType) base.searchParams.set("type", currentType)

    // preserve current sort if present
    const sort = new URLSearchParams(window.location.search).get("sort")
    if (sort) base.searchParams.set("sort", sort)

    // copy non-empty fields (checkbox arrays included)
    for (const [k, v] of fd.entries()) {
      if (v !== "") base.searchParams.append(k, v)
    }

    // Multi-select <select> (just in case you add any later)
    this.selectTargets.forEach(sel => {
      base.searchParams.delete(sel.name)
      Array.from(sel.selectedOptions).forEach(opt => {
        if (opt.value !== "") base.searchParams.append(sel.name, opt.value)
      })
    })

    return base.toString()
  }

  // URL with NO filters, but keep tab & sort
  unfilteredUrl() {
    const u = new URL(this.baseUrlValue, window.location.origin)

    // keep current tab
    const currentType = this.typeValue || new URLSearchParams(window.location.search).get("type")
    if (currentType) u.searchParams.set("type", currentType)

    // keep current sort
    const sort = new URLSearchParams(window.location.search).get("sort")
    if (sort) u.searchParams.set("sort", sort)

    return u.toString()
  }

  updateExportHref(url = null) {
    if (!this.hasExportLinkTarget) return
    const current = url || window.location.href
    const u = new URL(current)
    const exportUrl = new URL(this.baseUrlValue.replace(/\/?$/, "/export"), window.location.origin)
    u.searchParams.forEach((val, key) => exportUrl.searchParams.append(key, val))
    this.exportLinkTarget.setAttribute("href", exportUrl.toString())
  }

  updateCount() {
    if (!this.hasCountTarget) return
    let count = 0

    this.textTargets.forEach(i => { if (i.value.trim() !== "") count++ })
    this.numberTargets.forEach(i => { if (i.value !== "") count++ })
    this.selectTargets.forEach(s => {
      const selected = Array.from(s.selectedOptions).filter(o => o.value !== "")
      if (selected.length) count++
    })
    const byName = {}
    this.checkboxTargets.forEach(cb => {
      byName[cb.name] ||= 0
      if (cb.checked) byName[cb.name]++
    })
    Object.values(byName).forEach(n => { if (n > 0) count++ })

    this.countTarget.textContent = count > 0 ? `${count} Filters` : "Filters"
  }
}
